import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useNavigate, useParams } from 'react-router-dom'
import { turmasService, tentativasService, TurmaInsert, profilesService } from '../services/database'
import { useAuth } from '../hooks/useAuth'
import {
    TrashIcon
} from '@heroicons/react/24/outline'

const TurmaFormPage = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const isNew = id === 'new' || !id

    const [loading, setLoading] = useState(!isNew)
    const [saving, setSaving] = useState(false)
    const [activeTab, setActiveTab] = useState<'geral' | 'alunos' | 'testes' | 'aulas' | 'resultados'>('geral')

    // Form Data
    const [formData, setFormData] = useState<TurmaInsert>({
        nome: '',
        descricao: '',
        created_by: user?.id
    })

    // Related Data (Client side cache for simplicity in MVP)
    const [alunos, setAlunos] = useState<any[]>([])
    const [testes, setTestes] = useState<any[]>([])
    const [aulas, setAulas] = useState<any[]>([])

    useEffect(() => {
        if (!isNew && id) {
            fetchTurmaData(id)
        }
    }, [id])

    const fetchTurmaData = async (turmaId: string) => {
        try {
            setLoading(true)
            const turma = await turmasService.getById(turmaId)
            if (turma) {
                setFormData({
                    nome: turma.nome,
                    descricao: turma.descricao,
                    created_by: turma.created_by
                })

                // Fetch relations
                const [alunosData, testesData, aulasData] = await Promise.all([
                    turmasService.getAlunos(turmaId),
                    turmasService.getTestes(turmaId),
                    turmasService.getAulas(turmaId)
                ])
                setAlunos(alunosData)
                setTestes(testesData)
                setAulas(aulasData)
            }
        } catch (error) {
            console.error('Erro ao carregar turma', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSaveBasic = async (e: React.FormEvent) => {
        e.preventDefault()
        setSaving(true)
        try {
            if (isNew) {
                const newTurma = await turmasService.create({
                    ...formData,
                    created_by: user?.id
                })
                // Redirect to edit mode to enable tabs
                navigate(`/turmas/${newTurma.id}`, { replace: true })
            } else if (id) {
                await turmasService.update(id, formData)
                alert('Turma atualizada com sucesso!')
            }
        } catch (error) {
            console.error('Erro ao salvar:', error)
            alert('Erro ao salvar turma.')
        } finally {
            setSaving(false)
        }
    }


    if (loading) return <div>Carregando...</div>

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">
                    {isNew ? 'Nova Turma' : `Turma: ${formData.nome}`}
                </h1>
                <button
                    onClick={() => navigate('/turmas')}
                    className="text-gray-600 hover:text-gray-900"
                >
                    Voltar
                </button>
            </div>

            {/* Tabs */}
            {!isNew && (
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        {['geral', 'alunos', 'testes', 'aulas', 'resultados'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab as any)}
                                className={`
                                    whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm
                                    ${activeTab === tab
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                                `}
                            >
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                        ))}
                    </nav>
                </div>
            )}

            {/* Content */}
            <div className="bg-white rounded-lg shadow p-6">

                {/* GERAL TAB */}
                {activeTab === 'geral' && (
                    <form onSubmit={handleSaveBasic} className="space-y-6 max-w-lg">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Nome da Turma</label>
                            <input
                                type="text"
                                required
                                value={formData.nome}
                                onChange={e => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Descrição</label>
                            <textarea
                                value={formData.descricao || ''}
                                onChange={e => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                                rows={3}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                            />
                        </div>
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={saving}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                {saving ? 'Salvando...' : 'Salvar Alterações'}
                            </button>
                        </div>
                    </form>
                )}

                {/* ALUNOS TAB */}
                {activeTab === 'alunos' && (
                    <ManageAlunos turmaId={id!} currentAlunos={alunos} onUpdate={() => fetchTurmaData(id!)} />
                )}

                {/* TESTES TAB */}
                {activeTab === 'testes' && (
                    <ManageTestes turmaId={id!} currentTestes={testes} onUpdate={() => fetchTurmaData(id!)} />
                )}

                {/* AULAS TAB */}
                {activeTab === 'aulas' && (
                    <ManageAulas turmaId={id!} currentAulas={aulas} onUpdate={() => fetchTurmaData(id!)} />
                )}

                {/* RESULTADOS TAB */}
                {activeTab === 'resultados' && (
                    <ManageResultados turmaId={id!} />
                )}

            </div>
        </div>
    )
}

// --- Sub Components ---

const ManageAlunos = ({ turmaId, currentAlunos, onUpdate }: { turmaId: string, currentAlunos: any[], onUpdate: () => void }) => {
    const [allStudents, setAllStudents] = useState<any[]>([])
    const [selectedStudentId, setSelectedStudentId] = useState('')

    useEffect(() => {
        profilesService.getByRole('aluno').then(setAllStudents)
    }, [])

    const handleAdd = async () => {
        if (!selectedStudentId) return
        try {
            await turmasService.addAluno(turmaId, selectedStudentId)
            onUpdate()
            setSelectedStudentId('')
        } catch (e) {
            console.error(e)
            alert('Erro ao adicionar aluno')
        }
    }

    const handleRemove = async (alunoId: string) => {
        if (!window.confirm('Remover aluno desta turma?')) return
        try {
            await turmasService.removeAluno(turmaId, alunoId)
            onUpdate()
        } catch (e) {
            console.error(e)
        }
    }

    // Filter out already added students
    const availableStudents = allStudents.filter(s => !currentAlunos.find((ca: any) => ca.aluno_id === s.id))

    return (
        <div className="space-y-6">
            <div className="flex gap-4">
                <select
                    className="flex-1 rounded-md border border-gray-300 p-2"
                    value={selectedStudentId}
                    onChange={e => setSelectedStudentId(e.target.value)}
                >
                    <option value="">Selecione um aluno para adicionar...</option>
                    {availableStudents.map(s => (
                        <option key={s.id} value={s.id}>{s.full_name || 'Sem Nome'}</option>
                    ))}
                </select>
                <button
                    onClick={handleAdd}
                    disabled={!selectedStudentId}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                >
                    Adicionar
                </button>
            </div>

            <div className="border rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full text-left bg-white min-w-[500px]">
                    <thead className="bg-gray-50 border-b">
                        <tr>
                            <th className="p-4 font-medium text-gray-600">Nome</th>
                            <th className="p-4 font-medium text-gray-600 w-24">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {currentAlunos.map(item => (
                            <tr key={item.id}>
                                <td className="p-4">{item.aluno?.full_name || 'Usuário Desconhecido'}</td>
                                <td className="p-4">
                                    <button onClick={() => handleRemove((item as any).aluno_id)} className="text-red-600 hover:text-red-800">
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {currentAlunos.length === 0 && (
                            <tr><td colSpan={2} className="p-4 text-center text-gray-500">Nenhum aluno nesta turma.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

// Duplicate logic for Testes/Aulas for speed (Generic approach would be better but explicit is less error prone here)

import { testesService, aulasService } from '../services/database'

const ManageTestes = ({ turmaId, currentTestes, onUpdate }: { turmaId: string, currentTestes: any[], onUpdate: () => void }) => {
    const [allTestes, setAllTestes] = useState<any[]>([])
    const [selectedTesteId, setSelectedTesteId] = useState('')

    useEffect(() => {
        // Fetch only published tests for assignment? Or all? Usually published.
        testesService.getPublished().then(setAllTestes)
    }, [])

    const handleAdd = async () => {
        if (!selectedTesteId) return
        try {
            await turmasService.addTeste(turmaId, selectedTesteId)
            onUpdate()
            setSelectedTesteId('')
        } catch (e) {
            console.error(e)
            alert('Erro ao adicionar teste')
        }
    }

    const handleRemove = async (testeId: string) => {
        if (!window.confirm('Remover teste desta turma?')) return
        try {
            await turmasService.removeTeste(turmaId, testeId)
            onUpdate()
        } catch (e) {
            console.error(e)
        }
    }

    const availableTestes = allTestes.filter(t => !currentTestes.find(ct => ct.teste_id === t.id))

    return (
        <div className="space-y-6">
            <div className="flex gap-4">
                <select
                    className="flex-1 rounded-md border border-gray-300 p-2"
                    value={selectedTesteId}
                    onChange={e => setSelectedTesteId(e.target.value)}
                >
                    <option value="">Selecione um simulado para adicionar...</option>
                    {availableTestes.map(t => (
                        <option key={t.id} value={t.id}>{t.title}</option>
                    ))}
                </select>
                <button
                    onClick={handleAdd}
                    disabled={!selectedTesteId}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                >
                    Adicionar
                </button>
            </div>

            <div className="border rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full text-left bg-white min-w-[500px]">
                    <thead className="bg-gray-50 border-b">
                        <tr>
                            <th className="p-4 font-medium text-gray-600">Simulado</th>
                            <th className="p-4 font-medium text-gray-600 w-24">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {currentTestes.map(item => (
                            <tr key={item.id}>
                                <td className="p-4">{item.teste?.title || 'Simulado Removido'}</td>
                                <td className="p-4">
                                    <button onClick={() => handleRemove(item.teste_id)} className="text-red-600 hover:text-red-800">
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {currentTestes.length === 0 && (
                            <tr><td colSpan={2} className="p-4 text-center text-gray-500">Nenhum simulado vinculado.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

const ManageAulas = ({ turmaId, currentAulas, onUpdate }: { turmaId: string, currentAulas: any[], onUpdate: () => void }) => {
    const [allAulas, setAllAulas] = useState<any[]>([])
    const [selectedAulaId, setSelectedAulaId] = useState('')

    useEffect(() => {
        aulasService.getAll().then(setAllAulas)
    }, [])

    const handleAdd = async () => {
        if (!selectedAulaId) return
        try {
            await turmasService.addAula(turmaId, selectedAulaId)
            onUpdate()
            setSelectedAulaId('')
        } catch (e) {
            console.error(e)
            alert('Erro ao adicionar aula')
        }
    }

    const handleRemove = async (aulaId: string) => {
        if (!window.confirm('Remover aula desta turma?')) return
        try {
            await turmasService.removeAula(turmaId, aulaId)
            onUpdate()
        } catch (e) {
            console.error(e)
        }
    }

    const availableAulas = allAulas.filter(a => !currentAulas.find(ca => ca.aula_id === a.id))

    return (
        <div className="space-y-6">
            <div className="flex gap-4">
                <select
                    className="flex-1 rounded-md border border-gray-300 p-2"
                    value={selectedAulaId}
                    onChange={e => setSelectedAulaId(e.target.value)}
                >
                    <option value="">Selecione uma aula para adicionar...</option>
                    {availableAulas.map(a => (
                        <option key={a.id} value={a.id}>{a.title}</option>
                    ))}
                </select>
                <button
                    onClick={handleAdd}
                    disabled={!selectedAulaId}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                >
                    Adicionar
                </button>
            </div>

            <div className="border rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full text-left bg-white min-w-[500px]">
                    <thead className="bg-gray-50 border-b">
                        <tr>
                            <th className="p-4 font-medium text-gray-600">Aula</th>
                            <th className="p-4 font-medium text-gray-600 w-24">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {currentAulas.map(item => (
                            <tr key={item.id}>
                                <td className="p-4">{item.aula?.title || 'Aula Removida'}</td>
                                <td className="p-4">
                                    <button onClick={() => handleRemove(item.aula_id)} className="text-red-600 hover:text-red-800">
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {currentAulas.length === 0 && (
                            <tr><td colSpan={2} className="p-4 text-center text-gray-500">Nenhuma aula vinculada.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}



// --- Sub Components ---

const ManageResultados = ({ turmaId }: { turmaId: string }) => {
    const [loading, setLoading] = useState(true)
    const [results, setResults] = useState<any[]>([])

    // We need students and tests to display the grid elegantly.
    // Or just a flat list of "Latest Attempt per Student per Test".

    useEffect(() => {
        fetchResults()
    }, [turmaId])

    const fetchResults = async () => {
        try {
            setLoading(true)

            // 1. Get Students in Turma
            const students = await turmasService.getAlunos(turmaId)
            // Fix: ensure ID exists. If null, ignore.
            const studentIds = students.map((s: any) => s.aluno_id).filter(id => id !== null) as string[]

            // 2. Get Testes in Turma
            const testes = await turmasService.getTestes(turmaId)
            const testeIds = testes.map((t: any) => t.teste_id)

            if (studentIds.length === 0 || testeIds.length === 0) {
                setResults([])
                setLoading(false)
                return
            }

            // 3. Get Attempts for these students and tests
            // We'll fetch all attempts that match these criteria.
            const { data: attempts, error } = await supabase
                .from('tentativas_testes')
                .select(`
                    *,
                    teste:testes(title),
                    aluno:profiles!tentativas_testes_user_id_fkey(full_name)
                `) // Removed email from profile select as it might not be there
                .in('user_id', studentIds)
                .in('teste_id', testeIds)
                .order('created_at', { ascending: false })

            if (error) throw error

            // Map data to friendly format
            // We want the LATEST attempt for each (student, test) pair?
            // Or show history?
            // Usually "Current Status" matters.

            const enriched = attempts?.map((att: any) => {
                const studentProfile = students.find((s: any) => s.aluno_id === att.user_id) as any
                // Fallback to fetch from join if available, or 'Desconhecido'
                const name = studentProfile?.aluno?.full_name || att.aluno?.full_name || 'Desconhecido'

                // Test title from local testes list or join
                // const testeInfo = testes.find(t => t.teste_id === att.teste_id)?.teste
                const title = att.teste?.title || 'Desconhecido'

                return {
                    ...att,
                    studentName: name,
                    testeTitle: title
                }
            })

            setResults(enriched || [])

        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }

    const handleAllowRetake = async (attemptId: string) => {
        if (!window.confirm('Permitir que o aluno refaça este teste? A tentativa atual será arquivada.')) return
        try {
            await tentativasService.archive(attemptId)
            fetchResults() // Refresh
        } catch (e) {
            console.error(e)
            alert('Erro ao arquivar tentativa')
        }
    }

    if (loading) return <div>Carregando resultados...</div>

    return (
        <div className="space-y-6">
            <div className="border rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full text-left bg-white text-sm min-w-[600px]">
                    <thead className="bg-gray-50 border-b">
                        <tr>
                            <th className="p-4 font-medium text-gray-600">Aluno</th>
                            <th className="p-4 font-medium text-gray-600">Simulado</th>
                            <th className="p-4 font-medium text-gray-600">Nota</th>
                            <th className="p-4 font-medium text-gray-600">Status</th>
                            <th className="p-4 font-medium text-gray-600">Data</th>
                            <th className="p-4 font-medium text-gray-600">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {results.map(r => (
                            <tr key={r.id} className={r.is_archived ? 'bg-gray-50 opacity-60' : ''}>
                                <td className="p-4">{r.studentName}</td>
                                <td className="p-4">{r.testeTitle}</td>
                                <td className="p-4">
                                    <span className={`px-2 py-1 rounded text-xs font-bold ${r.nota >= 700 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                        }`}>
                                        {r.nota}
                                    </span>
                                </td>
                                <td className="p-4">
                                    {r.is_archived ? (
                                        <span className="text-gray-500">Arquivado</span>
                                    ) : (
                                        <span className="text-green-600 font-medium">Válido</span>
                                    )}
                                </td>
                                <td className="p-4 text-gray-500">
                                    {new Date(r.created_at).toLocaleDateString()}
                                </td>
                                <td className="p-4">
                                    {!r.is_archived && (
                                        <button
                                            onClick={() => handleAllowRetake(r.id)}
                                            className="text-blue-600 hover:underline text-xs"
                                        >
                                            Permitir Retentativa
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {results.length === 0 && (
                            <tr><td colSpan={6} className="p-4 text-center text-gray-500">Nenhum resultado encontrado.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default TurmaFormPage
